"use strict";
/**
 * <TextareaAutosize />
 * internal fork from https://github.com/andreypopp/react-textarea-autosize
 * extra function:
 * - no more resizing once the user changes the height manually
 * - pinning height when setting row property
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var PropTypes = require("prop-types");
var calculate_node_height_1 = require("./calculate_node_height");
var is_browser_1 = require("./is_browser");
var nodash_1 = require("../../../utils/nodash");
// TODO: ResizeObserver currently throws when testing with
// Maybe a webpack/bundling issue?
// fixing this would allow mount testing and increase coverage
var resize_observer_polyfill_1 = require("resize-observer-polyfill");
var withContext_1 = require("../../../utils/hoc/withContext");
// IE11 has a problem with eval source maps, can be reproduced with:
// eval('"use strict"; var onNextFrame = window.cancelAnimationFrame; onNextFrame(4);')
// so we bind window as context in dev modes
var _a = is_browser_1.default && window.requestAnimationFrame
    ? process.env.NODE_ENV !== 'development'
        ? [window.requestAnimationFrame, window.cancelAnimationFrame]
        : [
            window.requestAnimationFrame.bind(window),
            window.cancelAnimationFrame.bind(window)
        ]
    : [setTimeout, clearTimeout], onNextFrame = _a[0], clearNextFrameAction = _a[1];
var TextareaAutosizeWithinContext = /** @class */ (function (_super) {
    __extends(TextareaAutosizeWithinContext, _super);
    function TextareaAutosizeWithinContext(props) {
        var _this = _super.call(this, props) || this;
        _this.resizeLock = false;
        _this.onRootDOMNode = function (node) {
            _this.rootDOMNode = node;
            if (_this.props.inputRef) {
                _this.props.inputRef(node);
            }
        };
        _this.onChange = function (event) {
            if (!_this.controlled) {
                _this.resizeComponent();
            }
            _this.props.onChange(event);
        };
        _this.resizeComponent = function (callback) {
            if (callback === void 0) { callback = nodash_1.noop; }
            if (typeof _this.rootDOMNode === 'undefined' || _this.state.disabled) {
                callback();
                return;
            }
            var nodeHeight = calculate_node_height_1.default(_this.rootDOMNode, _this.uid, _this.props.useCacheForDOMMeasurements, _this.props.minRows, _this.props.maxRows);
            if (nodeHeight === null) {
                callback();
                return;
            }
            var height = nodeHeight.height, minHeight = nodeHeight.minHeight, maxHeight = nodeHeight.maxHeight, rowCount = nodeHeight.rowCount;
            _this.rowCount = rowCount;
            if (_this.state.height !== height ||
                _this.state.minHeight !== minHeight ||
                _this.state.maxHeight !== maxHeight) {
                _this.setState({ height: height, minHeight: minHeight, maxHeight: maxHeight }, callback);
                return;
            }
            callback();
        };
        _this.state = {
            height: (props.style && props.style.height) || 0,
            minHeight: -Infinity,
            maxHeight: Infinity,
            disabled: false
        };
        _this.uid = props.ergoElements.idGenerator.next();
        _this.controlled = typeof props.value === 'string';
        return _this;
    }
    TextareaAutosizeWithinContext.prototype.render = function () {
        var _this = this;
        var _a = this.props, minRows = _a.minRows, maxRows = _a.maxRows, onHeightChange = _a.onHeightChange, useCacheForDOMMeasurements = _a.useCacheForDOMMeasurements, inputRef = _a.inputRef, ergoElements = _a.ergoElements, props = __rest(_a, ["minRows", "maxRows", "onHeightChange", "useCacheForDOMMeasurements", "inputRef", "ergoElements"]);
        // clone readonly props
        var otherProps = __assign({}, props);
        otherProps.style = __assign(__assign({}, props.style), { height: this.state.height });
        var maxHeight = Math.max(otherProps.style.maxHeight || Infinity, this.state.maxHeight);
        if (maxHeight < this.state.height) {
            otherProps.style.overflow = 'hidden';
        }
        return (
        // the div acts as a wrapper around the textarea to pick up events from
        // the resize observer, since the textarea won't work with a resize observer
        React.createElement("div", { ref: function (wrapper) {
                _this.wrapperRef = wrapper;
            } },
            React.createElement("textarea", __assign({}, otherProps, { onChange: this.onChange, ref: this.onRootDOMNode }))));
    };
    TextareaAutosizeWithinContext.prototype.componentDidMount = function () {
        var _this = this;
        this.resizeComponent();
        // Working around Firefox bug which runs resize listeners even when other JS is running at the same moment
        // causing competing rerenders (due to setState in the listener) in React.
        // More can be found here - facebook/react#6324
        this.resizeListener = function () {
            if (_this.resizeLock) {
                return;
            }
            _this.resizeLock = true;
            _this.resizeComponent(function () { return (_this.resizeLock = false); });
        };
        window.addEventListener('resize', this.resizeListener);
        // This resizeObserver checks the height of the div-wrapper
        // If this height differs from the calculated textarea size, the textarea was resized
        // by the user and the Autosize function will be disabled
        /* istanbul ignore if */
        if (typeof resize_observer_polyfill_1.default === 'function') {
            this.resizeObserver = new resize_observer_polyfill_1.default(function (entries) {
                for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
                    var entry = entries_1[_i];
                    var height = entry.contentRect.height;
                    if (height !== _this.state.height) {
                        _this.setState({ disabled: true });
                        _this.resizeObserver.disconnect();
                        _this.resizeObserver = null;
                    }
                }
            });
            this.resizeObserver.observe(this.wrapperRef);
        }
    };
    TextareaAutosizeWithinContext.prototype.componentDidUpdate = function (_prevProps, prevState) {
        var _this = this;
        this.clearNextFrame();
        this.onNextFrameActionId = onNextFrame(function () {
            _this.resizeComponent(function () {
                if (_this.state.height !== prevState.height) {
                    _this.props.onHeightChange(_this.state.height, _this);
                }
            });
        });
    };
    TextareaAutosizeWithinContext.prototype.componentWillUnmount = function () {
        this.clearNextFrame();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        window.removeEventListener('resize', this.resizeListener);
        calculate_node_height_1.purgeCache(this.uid);
    };
    TextareaAutosizeWithinContext.prototype.clearNextFrame = function () {
        clearNextFrameAction(this.onNextFrameActionId);
    };
    TextareaAutosizeWithinContext.propTypes = {
        value: PropTypes.string,
        onChange: PropTypes.func,
        onHeightChange: PropTypes.func,
        useCacheForDOMMeasurements: PropTypes.bool,
        minRows: PropTypes.number,
        maxRows: PropTypes.number,
        inputRef: PropTypes.func
    };
    TextareaAutosizeWithinContext.defaultProps = {
        onChange: nodash_1.noop,
        onHeightChange: nodash_1.noop,
        useCacheForDOMMeasurements: false
    };
    return TextareaAutosizeWithinContext;
}(React.Component));
exports.TextareaAutosizeWithinContext = TextareaAutosizeWithinContext;
exports.TextareaAutosize = withContext_1.withErgoElementsContext('TextareaAutosize', TextareaAutosizeWithinContext);
exports.default = exports.TextareaAutosize;
//# sourceMappingURL=textarea_autosize.js.map